import { useIsStoreInMobileOrderDriveThruStores } from 'features/store/hooks/use-is-store-in-mobile-order-drive-thru-stores';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { useMemoWithCompare } from 'hooks/use-memo-with-compare';
import { ServiceMode, ServiceModeStatus } from 'state/service-mode/types';
import { getLocalizedServiceMode } from 'utils/service-mode';

// Store the order and which service modes will display in the text.
const serviceModeOrder: ServiceMode[] = [
  ServiceMode.DRIVE_THRU,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.TAKEOUT,
  ServiceMode.EAT_IN,
  ServiceMode.CURBSIDE,
];

interface UseServiceModeTextOptions {
  restaurant: IRestaurant;
}

export const isCapable = (serviceModeStatus: ServiceModeStatus, storeNumber?: string | null) => (
  serviceMode: ServiceMode
): boolean => {
  const status = serviceModeStatus[serviceMode];
  const isMobileOrderDriveThruEnabledForStore = useIsStoreInMobileOrderDriveThruStores(storeNumber);
  if (
    serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU &&
    !isMobileOrderDriveThruEnabledForStore
  ) {
    return false;
  }

  if (!status) {
    return false;
  }

  return status.capable;
};

export const useServiceModeText = ({
  restaurant,
}: UseServiceModeTextOptions): string | undefined => {
  const { formatMessage } = useIntl();
  const { isServiceModeCapable } = useStoreAvailabilityFns();

  const serviceModes = serviceModeOrder.filter(serviceMode =>
    isServiceModeCapable(restaurant, serviceMode, restaurant.localTime)
  );

  return useMemoWithCompare(() => {
    if (!serviceModes.length) {
      return;
    }
    return serviceModes
      .map(serviceMode => getLocalizedServiceMode(serviceMode, formatMessage))
      .join(', ');
  }, [serviceModes]);
};
